import { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { HairProductKey } from "~/api/models/HairProductKey";
import { SkinProductKey } from "~/api/models/SkinProductKey";
import { useFetchOpportunity } from "~/api/salesForce/useFetchOpportunity";
import Dropdown from "~/components/atoms/Dropdown/Dropdown";
import { DropdownOption } from "~/components/atoms/Dropdown/Dropdown.types";
import Input from "~/components/atoms/Input/Input";
import DiscountCodeDropdown from "~/components/molecules/DiscountCodeDropdown/DiscountCodeDropdown";
import PricingModal from "~/components/molecules/PricingModal/PricingModal";
import useHairProducts from "~/hooks/useHairProducts";
import useSkinProducts from "~/hooks/useSkinProducts";
import { Product } from "~/stores/checkout/product";
import { useCheckoutStore } from "~/stores/checkout/useCheckoutStore";
import FreeProductsChevronNew from "./FreeProductsChevronNew";

interface DiscountModalNewProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const discountReasonOptions: DropdownOption[] = [
  { id: "none", title: "None" },
  { id: "public", title: "Public Discount" },
  { id: "individual", title: "Individual Discount" },
  { id: "freeProducts", title: "Free Products" },
];

const DiscountModalNew = ({ open, setOpen }: DiscountModalNewProps) => {
  const intl = useIntl();
  const { opportunityId } = useParams();
  const { data: opportunity } = useFetchOpportunity({
    id: opportunityId as string,
  });
  const [
    discountCode,
    referralCode,
    individualDiscountAmount,
    individualDiscountPercent,
    setDiscountCode,
    setReferralCode,
    setIndividualDiscountAmount,
    setIndividualDiscountPercent,
  ] = useCheckoutStore((state) => [
    state.discountCode,
    state.referralCode,
    state.individualDiscountAmount,
    state.individualDiscountPercent,
    state.setDiscountCode,
    state.setReferralCode,
    state.setIndividualDiscountAmount,
    state.setIndividualDiscountPercent,
  ]);
  const {
    freePrpProduct,
    freeMesotherapyProduct,
    setFreePrpProduct,
    setMesotherapyProduct,
  } = useHairProducts();
  const { freeBotoxProduct, setFreeBotoxProduct } = useSkinProducts();

  // Local state
  const [selectedDiscountReason, setSelectedDiscountReason] = useState<
    DropdownOption | undefined
  >();
  const [selectedDiscountCode, setSelectedDiscountCode] = useState<
    string | null
  >(discountCode);
  const [selectedReferralCode, setSelectedReferralCode] = useState<
    string | null
  >(referralCode ?? null);
  const [selectedFreeProducts, setSelectedFreeProducts] = useState<Product[]>(
    []
  );
  const [selectedIndividualPercent, setSelectedIndividualPercent] =
    useState<number>(0);
  const [selectedIndividualAmount, setSelectedIndividualAmount] =
    useState<number>(0);
  const [lastEditedField, setLastEditedField] = useState<
    "percent" | "amount" | null
  >(null);

  const isPublicDiscountSelected = selectedDiscountReason?.id === "public";
  const isIndividualDiscountSelected =
    selectedDiscountReason?.id === "individual";
  const isFreeProductsDiscountSelected =
    selectedDiscountReason?.id === "freeProducts";

  const closeDropdown = () => {
    // Dispatch Escape key event to close the dropdown
    const event = new KeyboardEvent("keydown", { key: "Escape" });
    window.dispatchEvent(event);
  };

  const handleDiscountReasonChange = (option: DropdownOption) => {
    setSelectedDiscountReason(option);
    closeDropdown();

    // Clear other discount types when switching
    if (option.id !== "public") {
      setSelectedDiscountCode(null);
    }
    if (option.id !== "individual") {
      setSelectedIndividualPercent(0);
      setSelectedIndividualAmount(0);
    }
    if (option.id !== "freeProducts") {
      setSelectedFreeProducts([]);
    }
  };

  const handleIndividualDiscountPercentChange = (value: string) => {
    const percent = parseFloat(value) || 0;
    setSelectedIndividualPercent(percent);
    setLastEditedField("percent");
    setSelectedIndividualAmount(0); // Clear amount since percentage is now the source
  };

  const handleIndividualDiscountAmountChange = (value: string) => {
    const amount = parseFloat(value) || 0;
    setSelectedIndividualAmount(amount);
    setLastEditedField("amount");
    setSelectedIndividualPercent(0); // Clear percentage since amount is now the source
  };

  const handleApply = () => {
    setDiscountCode(selectedDiscountCode);
    setReferralCode(selectedReferralCode);

    // Only set individual discount values if individual discount is selected
    if (isIndividualDiscountSelected) {
      setIndividualDiscountAmount(
        selectedIndividualAmount > 0 ? selectedIndividualAmount : null
      );
      setIndividualDiscountPercent(
        selectedIndividualPercent > 0 ? selectedIndividualPercent : null
      );
    } else {
      // Clear individual discount values if individual discount is not selected
      setIndividualDiscountAmount(null);
      setIndividualDiscountPercent(null);
    }

    // Find and set free PRP product if selected
    const selectedFreePrpProduct = selectedFreeProducts.find(
      (product) => product.productCardKey === HairProductKey.freePrp
    );
    if (selectedFreePrpProduct) {
      setFreePrpProduct(selectedFreePrpProduct);
    } else if (freePrpProduct) {
      // Remove if it was previously selected but not in current selection
      setFreePrpProduct(undefined);
    }

    // Find and set free mesotherapy product if selected
    const selectedFreeMesotherapyProduct = selectedFreeProducts.find(
      (product) => product.productCardKey === HairProductKey.freeMesotherapy
    );
    if (selectedFreeMesotherapyProduct) {
      setMesotherapyProduct(selectedFreeMesotherapyProduct);
    } else if (freeMesotherapyProduct) {
      // Remove if it was previously selected but not in current selection
      setMesotherapyProduct(undefined);
    }

    // Find and set free botox product if selected
    const selectedFreeBotoxProduct = selectedFreeProducts.find(
      (product) => product.productCardKey === SkinProductKey.freeBotox
    );
    if (selectedFreeBotoxProduct) {
      setFreeBotoxProduct(selectedFreeBotoxProduct);
    } else if (freeBotoxProduct) {
      // Remove if it was previously selected but not in current selection
      setFreeBotoxProduct(undefined);
    }

    setOpen(false);
  };

  const onOpenChange = (open: boolean) => {
    setOpen(open);
  };

  // Initialize local state when modal opens
  useEffect(() => {
    if (open) {
      console.log("openTriggered", open);
      if (discountCode && selectedDiscountCode === null) {
        setSelectedDiscountCode(discountCode);
      }
      if (referralCode && selectedReferralCode === null) {
        setSelectedReferralCode(referralCode);
      }
      if (individualDiscountAmount && selectedIndividualAmount === 0) {
        setSelectedIndividualAmount(individualDiscountAmount ?? 0);
      }
      if (individualDiscountPercent && selectedIndividualPercent === 0) {
        setSelectedIndividualPercent(individualDiscountPercent ?? 0);
      }

      // Initialize selectedFreeProducts with currently selected free products
      const currentFreeProducts: Product[] = [];
      if (freePrpProduct) {
        currentFreeProducts.push(freePrpProduct);
      }
      if (freeMesotherapyProduct) {
        currentFreeProducts.push(freeMesotherapyProduct);
      }
      if (freeBotoxProduct) {
        currentFreeProducts.push(freeBotoxProduct);
      }
      setSelectedFreeProducts(currentFreeProducts);

      // Determine discount reason based on current state
      let initialDiscountReason = discountReasonOptions[0]; // Default to "public"

      if (currentFreeProducts.length > 0) {
        // If there are free products, set to "freeProducts"
        initialDiscountReason =
          discountReasonOptions.find(
            (option) => option.id === "freeProducts"
          ) || discountReasonOptions[0];
      } else if (discountCode) {
        // If there's a discount code, set to "public"
        initialDiscountReason =
          discountReasonOptions.find((option) => option.id === "public") ||
          discountReasonOptions[0];
      } else if (
        (individualDiscountAmount && individualDiscountAmount > 0) ||
        (individualDiscountPercent && individualDiscountPercent > 0)
      ) {
        initialDiscountReason =
          discountReasonOptions.find((option) => option.id === "individual") ||
          discountReasonOptions[0];
      }

      setSelectedDiscountReason(initialDiscountReason);

      // Reset the last edited field when modal opens
      setLastEditedField(null);
    }
  }, [
    open,
    discountCode,
    referralCode,
    freePrpProduct,
    freeMesotherapyProduct,
    freeBotoxProduct,
  ]);

  const discountPercentageCalculatedFromAmount = () => {
    if (opportunity?.amount && selectedIndividualAmount > 0) {
      const discountPercentage =
        (selectedIndividualAmount / opportunity?.amount) * 100;
      return Number(discountPercentage.toFixed(2));
    }
    return 0;
  };

  const discountAmountCalculatedFromPercentage = () => {
    if (opportunity?.amount && selectedIndividualPercent > 0) {
      const discountAmount =
        (opportunity?.amount * selectedIndividualPercent) / 100;
      return Number(discountAmount.toFixed(2));
    }
    return 0;
  };

  const discountPercentageToShow = () => {
    // If user is editing percentage or percentage was last edited, show the actual percentage
    if (
      lastEditedField === "percent" ||
      (selectedIndividualPercent > 0 && lastEditedField !== "amount")
    ) {
      return selectedIndividualPercent;
    }
    // Otherwise show calculated percentage from amount
    return discountPercentageCalculatedFromAmount();
  };

  const discountAmountToShow = () => {
    // If user is editing amount or amount was last edited, show the actual amount
    if (
      lastEditedField === "amount" ||
      (selectedIndividualAmount > 0 && lastEditedField !== "percent")
    ) {
      return selectedIndividualAmount;
    }
    // Otherwise show calculated amount from percentage
    return discountAmountCalculatedFromPercentage();
  };

  return (
    <PricingModal
      open={open}
      setOpen={onOpenChange}
      title={intl.formatMessage({ id: "discount.modal.title" })}
      buttonText={intl.formatMessage({ id: "discount.modal.apply" })}
      buttonAction={handleApply}
      className="!max-w-lg"
    >
      <div className="flex flex-col gap-6">
        {/* Discount Reason Selection */}
        <div>
          <label className="block text-sm font-medium mb-2">
            {intl.formatMessage({ id: "discount.modal.reason" })}
          </label>
          <Dropdown
            options={discountReasonOptions}
            value={selectedDiscountReason}
            onChange={handleDiscountReasonChange}
            className="w-full"
          />
        </div>

        {/* Public Discount Section */}
        {isPublicDiscountSelected && (
          <div>
            <DiscountCodeDropdown
              selectedDiscountCode={selectedDiscountCode}
              setSelectedDiscountCode={setSelectedDiscountCode}
            />
          </div>
        )}

        {/* Individual Discount Section */}
        {isIndividualDiscountSelected && (
          <div>
            <div className="flex justify-start gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  {intl.formatMessage({ id: "discount.modal.percent" })}
                </label>
                <Input
                  type="number"
                  placeholder={intl.formatMessage({
                    id: "discount.modal.percentPlaceholder",
                  })}
                  value={
                    discountPercentageToShow() > 0
                      ? discountPercentageToShow().toString()
                      : ""
                  }
                  onChange={handleIndividualDiscountPercentChange}
                  min="0"
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  {intl.formatMessage({ id: "discount.modal.amount" })}
                </label>
                <Input
                  type="number"
                  placeholder={intl.formatMessage({
                    id: "discount.modal.amountPlaceholder",
                  })}
                  value={
                    discountAmountToShow() > 0
                      ? discountAmountToShow().toString()
                      : ""
                  }
                  onChange={handleIndividualDiscountAmountChange}
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
          </div>
        )}

        {/* Free Products Section */}
        {isFreeProductsDiscountSelected && (
          <div>
            <FreeProductsChevronNew
              selectedFreeProducts={selectedFreeProducts}
              setSelectedFreeProducts={setSelectedFreeProducts}
            />
          </div>
        )}

        {/* Referral Code Section */}
        <div>
          <label className="block text-sm font-medium mb-2">
            {intl.formatMessage({ id: "discount.modal.referralCode" })}
          </label>
          <Input
            placeholder={intl.formatMessage({
              id: "discount.modal.referralCodePlaceholder",
            })}
            value={selectedReferralCode ?? ""}
            onChange={setSelectedReferralCode}
            className="w-full"
          />
        </div>
      </div>
    </PricingModal>
  );
};

export default DiscountModalNew;
